// Price book (M5 Finance). Loads the price-list items + tax rates once, then keeps them fresh
// off the price.changed SSE — mirroring ClinicsPage's load / live-refresh / queued-mutate shape.
// The "Add / update price" panel upserts a (service, grade) price through the offline queue; the
// price.changed event reconciles the table (our own replay or another user's edit).
import { useCallback, useEffect, useState } from 'react';
import {
  type PriceListItemDto,
  type TaxRateDto,
  type CaseGrade,
  CASE_GRADES,
  CASE_GRADE_LABELS,
  upsertPriceItemSchema,
  formatMoney,
} from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Card, PageHeader, Pill } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

// Parse a major-unit text input (e.g. "1500.50") into integer minor units. NaN → 0.
function toMinor(major: string): number {
  const n = Number(major);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function PriceListPage() {
  const can = useCan();
  const manage = can('invoice:manage');
  const [items, setItems] = useState<PriceListItemDto[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRateDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    api
      .get<PriceListItemDto[]>('/pricing/items')
      .then(setItems)
      .catch(() => setError(t('finance.price.loadError')));
    api
      .get<TaxRateDto[]>('/pricing/tax-rates')
      .then(setTaxRates)
      .catch(() => setError(t('finance.price.loadError')));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A price was created or updated somewhere (or our own queued edit replayed) — pull fresh truth.
  useLiveEvent('price.changed', () => {
    setQueued(false);
    load();
  });

  return (
    <div>
      <PageHeader title={t('finance.price.title')} subtitle={t('finance.price.subtitle')} />

      <div className="mb-4 flex items-center gap-2">
        {manage && (
          <Button className="ml-auto" onClick={() => setShowModal(true)}>
            {t('finance.price.add')}
          </Button>
        )}
      </div>

      {queued && (
        <div className="mb-3 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          {t('sync.queued')}
        </div>
      )}
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">{t('finance.price.col.service')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.price.col.grade')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.price.col.unitPrice')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.price.col.tax')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.price.col.active')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted">
                  {t('finance.price.empty')}
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-2">{item.service}</td>
                <td className="px-4 py-2">
                  {item.grade ? CASE_GRADE_LABELS[item.grade] : t('finance.price.defaultGrade')}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {formatMoney(item.unitPrice, item.currency)}
                </td>
                <td className="px-4 py-2">{item.taxRateCode ?? '-'}</td>
                <td className="px-4 py-2">
                  <Pill tone={item.isActive ? 'good' : 'neutral'}>
                    {item.isActive
                      ? t('finance.price.status.active')
                      : t('finance.price.status.inactive')}
                  </Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {manage && (
        <PriceModal
          open={showModal}
          taxRates={taxRates}
          onClose={() => setShowModal(false)}
          onSaved={(deferred) => {
            if (deferred) setQueued(true);
            else load();
          }}
        />
      )}
    </div>
  );
}

// Inline upsert panel: a (service, grade) price is entered in MAJOR units and converted to minor
// before validation, then posted through the offline queue.
function PriceModal({
  open,
  taxRates,
  onClose,
  onSaved,
}: {
  open: boolean;
  taxRates: TaxRateDto[];
  onClose: () => void;
  onSaved: (deferred: boolean) => void;
}) {
  const [service, setService] = useState('');
  const [grade, setGrade] = useState<CaseGrade | ''>('');
  const [unitPrice, setUnitPrice] = useState('');
  const [taxRateCode, setTaxRateCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setService('');
    setGrade('');
    setUnitPrice('');
    setTaxRateCode('');
    setError(null);
    setSubmitting(false);
  }, [open]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = upsertPriceItemSchema.safeParse({
      service: service.trim(),
      grade: grade === '' ? null : grade,
      unitPrice: toMinor(unitPrice),
      taxRateCode: taxRateCode || null,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('finance.price.form.invalid'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post<PriceListItemDto>('/pricing/items', parsed.data, { queue: true });
      onSaved(isDeferred(res));
      onClose();
    } catch {
      setError(t('finance.price.form.saveError'));
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-screen w-full max-w-lg overflow-y-auto p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">{t('finance.price.form.title')}</h2>
        <form className="space-y-4" onSubmit={submit}>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
              {t('finance.price.form.service')}
            </label>
            <input
              className={inputClass}
              value={service}
              onChange={(e) => setService(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.price.form.grade')}
              </label>
              <select
                className={inputClass}
                value={grade}
                onChange={(e) => setGrade(e.target.value as CaseGrade | '')}
              >
                <option value="">{t('finance.price.defaultGrade')}</option>
                {CASE_GRADES.map((g) => (
                  <option key={g} value={g}>
                    {CASE_GRADE_LABELS[g]}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.price.form.unitPrice')}
              </label>
              <input
                className={inputClass}
                type="number"
                min="0"
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('finance.price.form.tax')}</label>
            <select
              className={inputClass}
              value={taxRateCode}
              onChange={(e) => setTaxRateCode(e.target.value)}
            >
              <option value="">{t('finance.price.form.taxNone')}</option>
              {taxRates.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('finance.price.form.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('finance.price.form.saving') : t('finance.price.form.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
